import { Dispatch } from "react";
import { AppState } from "./configureStore";
import { createSlice, PayloadAction, AnyAction } from '@reduxjs/toolkit';
import { client } from "../App";
import { CmsFragment, GetCmsFragmentCollection } from "../DCR.dtos";
import { IEnumLoadableState, DataLoadState } from "../definitions/IEnumLoadableState";

export interface ICmsFragmentState extends IEnumLoadableState {
    fragments: CmsFragment[];
}

export interface ICmsState {
    manageContributions: ICmsFragmentState,
    contactUs: ICmsFragmentState,
    logout: ICmsFragmentState
}

export const initialCmsState: ICmsState = {
    manageContributions: { loadState: DataLoadState.None } as ICmsFragmentState,
    contactUs: { loadState: DataLoadState.None } as ICmsFragmentState,
    logout: { loadState: DataLoadState.None } as ICmsFragmentState
};

const slice = createSlice({
    name: 'cmsData',
    initialState: initialCmsState,
    reducers: {
        resetState: () => {
            return {
                ...initialCmsState
            }
        },
        requestManageContributionsCmsData: (state: ICmsState) => {
            return {
                ...state,
                manageContributions: {
                    ...state.manageContributions,
                    loadState: DataLoadState.Loading
                }
            }
        },
        receiveManageContributionsCmsData: (state: ICmsState, action: PayloadAction<{ fragments: CmsFragment[] }>) => {
            const { fragments } = action.payload;
            return {
                ...state,
                manageContributions: {
                    ...state.manageContributions,
                    fragments,
                    loadState: DataLoadState.Loaded
                }
            }
        },
        receiveManageContributionsCmsDataError: (state: ICmsState) => {
            return {
                ...state,
                manageContributions: {
                    ...state.manageContributions,
                    loadState: DataLoadState.Error
                }

            }
        },
        requestContactUsCmsData: (state: ICmsState) => {
            return {
                ...state,
                contactUs: {
                    ...state.contactUs,
                    loadState: DataLoadState.Loading
                }
            }
        },
        receiveContactUsCmsData: (state: ICmsState, action: PayloadAction<{ fragments: CmsFragment[] }>) => {
            const { fragments } = action.payload;
            return {
                ...state,
                contactUs: {
                    ...state.contactUs,
                    fragments,
                    loadState: DataLoadState.Loaded
                }
            }
        },
        receiveContactUsCmsDataError: (state: ICmsState) => {
            return {
                ...state,
                contactUs: {
                    ...state.contactUs,
                    loadState: DataLoadState.Error
                }

            }
        },
        requestLogoutCmsData: (state: ICmsState) => {
            return {
                ...state,
                logout: {
                    ...state.logout,
                    loadState: DataLoadState.Loading
                }
            }
        },
        receiveLogoutCmsData: (state: ICmsState, action: PayloadAction<{ fragments: CmsFragment[] }>) => {
            const { fragments } = action.payload;
            return {
                ...state,
                logout: {
                    ...state.logout,
                    fragments,
                    loadState: DataLoadState.Loaded
                }
            }
        },
        receiveLogoutCmsDataError: (state: ICmsState) => {
            return {
                ...state,
                logout: {
                    ...state.logout,
                    loadState: DataLoadState.Error
                }

            }
        }
    }
})

const {
    resetState,
    requestManageContributionsCmsData,
    receiveManageContributionsCmsData,
    receiveManageContributionsCmsDataError,
    requestContactUsCmsData,
    receiveContactUsCmsData,
    receiveContactUsCmsDataError,
    requestLogoutCmsData,
    receiveLogoutCmsData,
    receiveLogoutCmsDataError
} = slice.actions;

function fetchManageContributionsCmsData() {
    return async (dispatch: Dispatch<AnyAction>, getState: () => AppState) => {
        const { loadState } = getState().cms.manageContributions;
        if (loadState === DataLoadState.Loaded || loadState === DataLoadState.Loading) {
            return;
        }

        dispatch(requestManageContributionsCmsData());

        try {

            const cmsData = await client.get(new GetCmsFragmentCollection({ path: "/ManageContributions" }));

            dispatch(receiveManageContributionsCmsData({ fragments: cmsData.fragments }));

        } catch (e) {
            dispatch(receiveManageContributionsCmsDataError());
            //console.log(e);
        }

    }
}

function fetchContactUsCmsData() {
    return async (dispatch: Dispatch<AnyAction>, getState: () => AppState) => {
        const { loadState } = getState().cms.contactUs;
        if (loadState === DataLoadState.Loaded || loadState === DataLoadState.Loading) {
            return;
        }

        dispatch(requestContactUsCmsData());

        try {

            const cmsData = await client.get(new GetCmsFragmentCollection({ path: "/ContactUs" }));

            dispatch(receiveContactUsCmsData({ fragments: cmsData.fragments }));

        } catch (e) {
            dispatch(receiveContactUsCmsDataError());
            //console.log(e);
        }

    }
}

function fetchLogoutCmsData() {
    return async (dispatch: Dispatch<AnyAction>, getState: () => AppState) => {
        const { loadState } = getState().cms.logout;
        if (loadState === DataLoadState.Loaded || loadState === DataLoadState.Loading) {
            return;
        }

        dispatch(requestLogoutCmsData());

        try {

            const cmsData = await client.get(new GetCmsFragmentCollection({ path: "/Logout" }));

            dispatch(receiveLogoutCmsData({ fragments: cmsData.fragments }));

        } catch (e) {
            dispatch(receiveLogoutCmsDataError());
            //console.log(e);
        }

    }
}



export const cmsActionCreators = {
    fetchManageContributionsCmsData,
    fetchContactUsCmsData,
    fetchLogoutCmsData,
    resetState
};

export const cmsReducer = slice.reducer;